import cn from 'classnames';
import styles from './ButtonGroup.module.css';

type Props = {
  className?: string;
  children: React.ReactNode;
  /**
   * Stretch the items to fill the group's width regardless of viewport. Groups already stretch at
   * `--size-breakpoint-xs` viewports; pass this when the group sits in a container that stacks at a
   * wider point (e.g. HeaderButtons' collapsed variant).
   */
  stretch?: boolean;
};

/**
 * {@link ButtonGroup} squashes a set of multiple buttons (provided as children)
 * next to each other, removing the border-radius and extra border between them.
 */
export function ButtonGroup({ className, children, stretch }: Props) {
  return (
    <div className={cn(styles.ButtonGroup, { [styles.ButtonGroup___stretch]: stretch }, className)}>{children}</div>
  );
}

type ItemProps = {
  children?: React.ReactNode;
  className?: string;
  /**
   * In a stretched group, keep this item at a readable minimum width while its siblings share the
   * rest — for a wide control (e.g. a select) that shouldn't collapse to an even fraction next to
   * icon buttons. No effect outside a `stretch` group.
   */
  primary?: boolean;
  /**
   * The inverse of `primary`: in a stretched group, keep this item at its content width instead of
   * growing to an even fraction — for a glyph-only cell (e.g. a split button's caret) that should
   * hug its neighbor rather than read as a peer control. No effect outside a `stretch` group.
   */
  compact?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

export function ButtonGroupItem({ children, className, primary, compact, ref }: ItemProps) {
  return (
    <div
      className={cn(className, styles.ButtonGroup_item, {
        [styles.ButtonGroup_item___primary]: primary,
        [styles.ButtonGroup_item___compact]: compact,
      })}
      ref={ref}
    >
      {children}
    </div>
  );
}
